// src/hooks/useSSE.js
import { useEffect, useRef, useCallback } from 'react';
import logger from '../utils/logger';

export const useSSE = (url, options = {}) => {
  const eventSourceRef = useRef(null);
  const listenersRef = useRef(new Map());
  const isConnectingRef = useRef(false);

  const {
    onOpen,
    onError,
    onClose,
    reconnectInterval = 3000,
    maxReconnectAttempts = Infinity,
    autoReconnect = true
  } = options;

  const reconnectAttempts = useRef(0);
  const shouldReconnect = useRef(true);

  const connect = useCallback(() => {
    if (!url) {
      logger.log('[SSE] No URL provided, skipping connection');
      return;
    }

    if (eventSourceRef.current?.readyState === EventSource.OPEN) {
      logger.log('[SSE] Already connected, skipping');
      return;
    }

    if (isConnectingRef.current) {
      logger.log('[SSE] Connection already in progress, skipping');
      return;
    }

    try {
      logger.log('[SSE] Connecting to:', url);
      isConnectingRef.current = true;
      eventSourceRef.current = new EventSource(url);

      eventSourceRef.current.onopen = (event) => {
        logger.log('[SSE] Connected successfully');
        isConnectingRef.current = false;
        reconnectAttempts.current = 0;
        onOpen?.(event);
      };

      eventSourceRef.current.onerror = (event) => {
        console.error('[SSE] Connection error:', event);
        isConnectingRef.current = false;
        onError?.(event);

        // Attempt to reconnect if enabled (with progressive backoff, max 30s)
        if (autoReconnect && shouldReconnect.current && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current += 1;
          const delay = Math.min(reconnectInterval * Math.pow(1.5, Math.min(reconnectAttempts.current - 1, 10)), 30000);
          logger.log(`[SSE] Reconnecting in ${Math.round(delay/1000)}s... (attempt ${reconnectAttempts.current})`);

          setTimeout(() => {
            if (shouldReconnect.current) {
              connect();
            }
          }, delay);
        }
      };

      // Set up listeners for all registered event types
      listenersRef.current.forEach((callback, eventType) => {
        eventSourceRef.current.addEventListener(eventType, callback);
      });

    } catch (error) {
      console.error('[SSE] Failed to create EventSource:', error);
      isConnectingRef.current = false;
      onError?.(error);
    }
  }, [url, onOpen, onError, autoReconnect, reconnectInterval, maxReconnectAttempts]);

  const disconnect = useCallback(() => {
    shouldReconnect.current = false;
    isConnectingRef.current = false;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      logger.log('[SSE] Disconnected');
      onClose?.();
    }
  }, [onClose]);

  const addEventListener = useCallback((eventType, callback) => {
    logger.log(`[SSE] Adding listener for event: ${eventType}`);
    listenersRef.current.set(eventType, callback);

    // If already connected, add the listener immediately
    if (eventSourceRef.current?.readyState === EventSource.OPEN) {
      eventSourceRef.current.addEventListener(eventType, callback);
    }
  }, []);

  const removeEventListener = useCallback((eventType) => {
    logger.log(`[SSE] Removing listener for event: ${eventType}`);
    const callback = listenersRef.current.get(eventType);

    if (callback && eventSourceRef.current) {
      eventSourceRef.current.removeEventListener(eventType, callback);
    }

    listenersRef.current.delete(eventType);
  }, []);

  const getConnectionState = useCallback(() => {
    if (!eventSourceRef.current) return 'CLOSED';

    switch (eventSourceRef.current.readyState) {
      case EventSource.CONNECTING:
        return 'CONNECTING';
      case EventSource.OPEN:
        return 'OPEN';
      case EventSource.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }, []);

  useEffect(() => {
    // First disconnect any existing connection when URL changes
    if (eventSourceRef.current) {
      logger.log('[SSE] URL changed, disconnecting existing connection');
      disconnect();
    }

    if (url) {
      // Small delay to ensure cleanup is complete
      const timeoutId = setTimeout(() => {
        connect();
      }, 100);
      return () => {
        clearTimeout(timeoutId);
        disconnect();
      };
    } else {
      disconnect();
    }
  }, [url, connect, disconnect]);

  return {
    connect,
    disconnect,
    addEventListener,
    removeEventListener,
    getConnectionState,
    isConnected: getConnectionState() === 'OPEN'
  };
};
